import {InstalledBaseNodeChildRule, InstalledBaseNodeRules} from "../codegen/graphql";

/**
 * Helpers for interpreting `InstalledBaseNode.nodeRules` (IBM-462).
 *
 * Semantics (see backend handoff / system-types-architecture.md):
 * - `nodeRules: null`  → the node has no structural rules (not SYSTEM/PRESSURE_LINE/ASSET).
 * - `childRules: null` → no whitelist restriction (unknown-typed system).
 * - `childRules: []`   → the node is a leaf; no children allowed at all.
 * - `limits: null`     → the child type has no count limit; only the whitelist applies.
 * - `limits.max: -1`   → no upper limit.
 * - `currentNodeCount` may exceed `limits.max` (grandfathered data) — never clamp it,
 *   just treat the type as full.
 */

export const getChildRule = (
    nodeRules: InstalledBaseNodeRules | null | undefined,
    nodeTypeKey: string
): InstalledBaseNodeChildRule | undefined =>
    nodeRules?.childRules?.find((rule) => rule.nodeTypeKey === nodeTypeKey);

/** Whether a child rule still has room for another node (whitelist membership assumed). */
export const childRuleHasRoom = (rule: InstalledBaseNodeChildRule): boolean =>
    !rule.limits || rule.limits.max === -1 || rule.currentNodeCount < rule.limits.max;

/**
 * Whether a child of the given type may be created under a node with these rules.
 * Only structural rules — permissions (`access.create`) are checked separately.
 */
export const canAddChildType = (
    nodeRules: InstalledBaseNodeRules | null | undefined,
    nodeTypeKey: string
): boolean => {
    if (!nodeRules || !nodeRules.childRules) return true;
    const rule = getChildRule(nodeRules, nodeTypeKey);
    if (!rule) return false;
    return childRuleHasRoom(rule);
};

/**
 * Whether the type is structurally allowed at all (whitelist), regardless of counts.
 * Used to hide (rather than disable) types that could never be created here.
 */
export const isChildTypeWhitelisted = (
    nodeRules: InstalledBaseNodeRules | null | undefined,
    nodeTypeKey: string
): boolean => {
    if (!nodeRules || !nodeRules.childRules) return true;
    return !!getChildRule(nodeRules, nodeTypeKey);
};

/**
 * Whether any child at all may be created under a node with these rules.
 * Drives the column "+" button: `false` with `childRules: []` means leaf (hide),
 * `false` with rules present means every allowed type is at its limit (disable).
 */
export const canAddAnyChild = (
    nodeRules: InstalledBaseNodeRules | null | undefined
): boolean => {
    if (!nodeRules || !nodeRules.childRules) return true;
    return nodeRules.childRules.some(childRuleHasRoom);
};

/** `childRules: []` — the node is a leaf and can never have children. */
export const isLeafNode = (
    nodeRules: InstalledBaseNodeRules | null | undefined
): boolean => nodeRules?.childRules?.length === 0;

/**
 * Whether child-creation affordances should be offered under this parent at all:
 * the column "+", the bottom create button and the empty-state CTAs.
 *
 * Covers structure and permission only — NOT count limits. Combine with
 * {@link canAddAnyChild} where a full parent should hide the control; the column header
 * deliberately keeps its "+" visible-but-disabled in that case so it can explain why.
 *
 * Root columns are excluded: companies are created through a different path.
 */
export const canOfferChildCreation = (
    isRoot: boolean,
    nodeRules: InstalledBaseNodeRules | null | undefined,
    canWrite: boolean
): boolean => !isRoot && !isLeafNode(nodeRules) && canWrite;
